import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { SongPicker, type Track } from "../components/SongPicker";
import { WIDE_WIDTHS, srcSetFor, sizesForHeights } from "../lib/images";
import { ApiError, api } from "../lib/api";
import { schoolPride } from "../lib/confetti";
import { useSession } from "../lib/session";

// Interleaved landscape [36, 31, 24, 8] and portrait [25, 26, 29, 27] frames.
// Each carries its real pixel size so the browser can reserve the frame's width
// from the aspect ratio *before* the image loads — otherwise the marquee's
// w-max track starts at zero width on a cold load and iOS Safari locks the
// -50% transform to that, leaving it stuck until a refresh warms the cache.
const CAROUSEL_IMAGES = [36, 25, 31, 26, 24, 29, 8, 27].map((n) => {
  const portrait = [25, 26, 29, 27].includes(n);
  return {
    src: `/engagement-shoot-preview/7.12.26_SP-${n}.webp`,
    w: portrait ? 1067 : 1600,
    h: portrait ? 1600 : 1067,
  };
});

// Guests come from the server (backed by apps/server/guest-list.csv). A "party"
// is a household — everyone who shares a Party value in the sheet.
type Member = { id: string; name: string };
type Match = { id: string; name: string; party: string };
type SavedResponse = {
  friday: number | null;
  saturday: number | null;
  dietary: string | null;
};
type SavedSong = { id?: string; name: string; artist?: string; url?: string };
type PartyData = {
  party: string;
  members: Member[];
  responses: Record<string, SavedResponse>;
  songs: Record<string, SavedSong>; // keyed by guest id
};

const DAYS = [
  {
    key: "friday",
    label: "Friday",
    date: "April 30",
    sub: "Welcome Party & Mehendi",
  },
  {
    key: "saturday",
    label: "Saturday",
    date: "May 1",
    sub: "Ceremonies & Reception",
  },
] as const;

type Attendance = boolean | null;
type MemberResponse = {
  attendance: Record<string, Attendance>;
  dietary: string;
};

function emptyResponse(): MemberResponse {
  const attendance: Record<string, Attendance> = {};
  DAYS.forEach((d) => {
    attendance[d.key] = null;
  });
  return { attendance, dietary: "" };
}

// A saved song comes back as the stored fields; widen it to a Track for the picker.
function songToTrack(s: SavedSong | undefined): Track | null {
  if (!s) return null;
  return {
    id: s.id ?? "",
    name: s.name,
    artists: s.artist ?? "",
    album: "",
    albumImage: null,
    uri: s.url ?? "",
  };
}

export function RSVP() {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [party, setParty] = useState<Member[] | null>(null);
  const [greeting, setGreeting] = useState("");
  const [responses, setResponses] = useState<Record<string, MemberResponse>>(
    {},
  );
  const [songs, setSongs] = useState<Record<string, Track | null>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const { refresh } = useSession();

  // Debounced name search against the CSV-backed guest list.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const r = await api.get<{ matches: Match[] }>(
          `/rsvp/search?q=${encodeURIComponent(q)}`,
        );
        setMatches(r.matches);
        setSearchError(null);
      } catch (e) {
        setMatches([]);
        if (e instanceof ApiError && e.status === 401) {
          // Session died under us (server restart, expired cookie). Re-checking
          // sends us back to the password gate — far better than telling a real
          // guest their name isn't on the list.
          void refresh();
        } else {
          setSearchError("Something went wrong - try again in a second.");
        }
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, refresh]);

  // A single yes anywhere in the party is worth celebrating. Confetti only
  // reads wrong when nobody is coming to anything.
  const anyAttending =
    party?.some((m) =>
      DAYS.some((d) => responses[m.id]?.attendance[d.key] === true),
    ) ?? false;

  // Celebrate the moment the confirmation appears — unless it's all nos.
  useEffect(() => {
    if (!submitted || !anyAttending) return;
    return schoolPride();
  }, [submitted, anyAttending]);

  // Snap to the top on each view change (open party, submit, reset) so the nav
  // stays in view instead of leaving you mid-card with the header scrolled off.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [party, submitted]);

  // Open a match's whole party, prefilling anything they've already answered.
  const openParty = async (match: Match) => {
    // Log that this guest looked themselves up — a "who's seen the site" signal.
    void api.post("/rsvp/seen", { guestId: match.id }).catch(() => {});
    try {
      const data = await api.get<PartyData>(
        `/rsvp/party?guest=${encodeURIComponent(match.id)}`,
      );
      const init: Record<string, MemberResponse> = {};
      const initSongs: Record<string, Track | null> = {};
      data.members.forEach((m) => {
        const saved = data.responses[m.id];
        const attendance: Record<string, Attendance> = {};
        DAYS.forEach((d) => {
          const v = saved?.[d.key as "friday" | "saturday"];
          attendance[d.key] = v === 1 ? true : v === 0 ? false : null;
        });
        init[m.id] = { attendance, dietary: saved?.dietary ?? "" };
        initSongs[m.id] = songToTrack(data.songs[m.id]);
      });
      setParty(data.members);
      setGreeting(match.name.split(" ")[0] ?? "");
      setResponses(init);
      setSongs(initSongs);
      setError(null);
    } catch {
      setError("We couldn't pull up your party — please try again.");
    }
  };

  const reset = () => {
    setParty(null);
    setQuery("");
    setMatches([]);
    setGreeting("");
    setResponses({});
    setSongs({});
    setError(null);
  };

  const submit = async () => {
    if (!party || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/rsvp", {
        responses: party.map((m) => {
          const r = responses[m.id] ?? emptyResponse();
          const t = songs[m.id] ?? null;
          return {
            guestId: m.id,
            friday: r.attendance.friday ?? null,
            saturday: r.attendance.saturday ?? null,
            dietary: r.dietary.trim() || undefined,
            song: t
              ? { id: t.id, name: t.name, artist: t.artists, url: t.uri }
              : null,
          };
        }),
      });
      setSubmitted(true);
      setError(null);
    } catch {
      setError("Something went wrong sending your RSVP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const setAttendance = (id: string, dayKey: string, value: boolean) =>
    setResponses((r) => {
      const prev = r[id] ?? emptyResponse();
      return {
        ...r,
        [id]: {
          ...prev,
          attendance: {
            ...prev.attendance,
            // Click the active choice again to clear it.
            [dayKey]: prev.attendance[dayKey] === value ? null : value,
          },
        },
      };
    });

  const setDietary = (id: string, dietary: string) =>
    setResponses((r) => {
      const prev = r[id] ?? emptyResponse();
      return { ...r, [id]: { ...prev, dietary } };
    });

  const complete =
    party?.every((m) =>
      DAYS.every((d) => responses[m.id]?.attendance[d.key] !== null),
    ) ?? false;

  // ── Confirmation ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="mx-auto max-w-xl py-24 text-center">
        <span
          aria-hidden="true"
          className="font-serif text-3xl text-dusty-rose"
        >
          ❃
        </span>
        <h1 className="mt-4 font-serif text-5xl text-pine">Thank you!</h1>
        <p className="mx-auto mt-4 max-w-sm font-sans text-lg font-normal tracking-[0.02em] text-pine">
          {anyAttending ? "We’ve got your RSVP!" : "We’ll miss you!"}
        </p>
        <p className="mx-auto mt-7 max-w-sm font-serif text-2xl text-pine sm:text-3xl">
          Want to play one of our{" "}
          <Link
            to="/games"
            className="text-midnight-teal underline decoration-midnight-teal/40 underline-offset-4 transition-colors hover:decoration-midnight-teal"
          >
            games
          </Link>
          ?
        </p>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            reset();
          }}
          className="mt-8 text-sm uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:text-pine"
        >
          Edit our response
        </button>
      </div>
    );
  }

  // ── Party registration ────────────────────────────────────────────────────
  if (party) {
    return (
      <div className="relative left-1/2 -mb-12 -mt-12 w-screen -translate-x-1/2 bg-dusty-teal px-3 pb-16 pt-8 sm:px-6 sm:pb-20 sm:pt-10">
        <div className="mx-auto max-w-2xl">
          <div
            aria-hidden="true"
            className="mx-auto h-10 w-full max-w-lg bg-no-repeat sm:max-w-xl"
            style={{
              backgroundImage: "url(/separators/flourish.svg)",
              backgroundSize: "100% 100%",
            }}
          />

          <div className="relative mt-8 bg-paper px-7 py-10 shadow-invite sm:px-12 sm:py-14">
            {/* A single hairline inset rule — a printed-paper edge. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-[8px] border border-midnight-teal/40 sm:inset-[16px]"
            />

            <div className="relative">
              <div className="text-center">
                <p className="text-base uppercase tracking-[0.3em] text-midnight-teal">
                  Your Party
                </p>
                <h1 className="mt-3 font-serif text-4xl text-pine sm:text-5xl">
                  {greeting ? `Hi, ${greeting}!` : "Hi there!"}
                </h1>
              </div>

              <div
                aria-hidden="true"
                className="mt-8 h-px bg-midnight-teal/30"
              />

              <div className="mt-2">
                {/* No rule between people — the dietary field ends in a
                    full-width underline of its own, and a second hairline a
                    few px below it just reads as a doubled line. The padding
                    on MemberCard does the separating instead. */}
                {party.map((m) => (
                  <Fragment key={m.id}>
                    <MemberCard
                      member={m}
                      response={responses[m.id] ?? emptyResponse()}
                      onDay={setAttendance}
                      onDietary={setDietary}
                    />
                  </Fragment>
                ))}
              </div>

              <div className="mt-8">
                <p className="font-serif text-2xl text-pine">
                  Optional: a song to get you on the dance floor!
                </p>
                <p className="mt-1 font-sans text-base text-pine/60">
                  We make no promises you'll hear it 😁
                </p>
                <div className="mt-5 space-y-5">
                  {party.map((m) => (
                    <div key={m.id}>
                      <p className="font-sans text-base font-medium text-pine">
                        {m.name.split(" ")[0]}
                      </p>
                      <div className="mt-2">
                        <SongPicker
                          value={songs[m.id] ?? null}
                          onChange={(t) =>
                            setSongs((prev) => ({ ...prev, [m.id]: t }))
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={!complete || submitting}
                className="mt-10 w-full bg-midnight-teal py-4 text-base font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? "Sending…" : "Send our RSVP"}
              </button>
              {error && (
                <p className="mt-3 text-center text-base text-dusty-rose">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Name search ───────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-xl pb-10 pt-0 sm:pb-16">
      <PhotoCarousel images={CAROUSEL_IMAGES} />

      <div className="mt-14 text-center">
        <img
          src="/separators/rsvp-flourish.svg"
          alt=""
          aria-hidden="true"
          className="mx-auto w-48 sm:w-56"
        />
        <h1 className="mt-4 font-serif text-5xl text-pine sm:text-6xl">RSVP</h1>
        <p className="mx-auto mt-4 max-w-md font-sans text-lg font-normal tracking-[0.02em] text-pine">
          Please let us know by October 30th whether you'll be able to attend.
          We hope to see you there!
        </p>
      </div>

      <form
        className="mx-auto mt-12 max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          const first = matches[0];
          if (first) openParty(first);
        }}
      >
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="type your first or last name…"
          className="w-full border-b border-pine/25 bg-transparent pb-2 text-center font-serif text-2xl text-pine outline-none transition placeholder:italic placeholder:text-pine/35 focus:border-midnight-teal"
        />
      </form>

      {query.trim().length >= 2 && (
        <div className="mx-auto mt-2 max-w-md">
          {searchError ? (
            <p className="mt-6 text-center italic text-dusty-rose">
              {searchError}
            </p>
          ) : matches.length === 0 ? (
            <p className="mt-6 text-center italic text-pine/55">
              No match for &ldquo;{query.trim()}&rdquo; — try your first or last
              name.
            </p>
          ) : (
            <ul className="divide-y divide-pine/10 border-b border-pine/10">
              {matches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => openParty(m)}
                    className="w-full py-4 text-center font-serif text-2xl text-pine transition-colors hover:text-midnight-teal"
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

type CarouselImage = { src: string; w: number; h: number };

/** Full-width, slowly-sliding filmstrip used as the RSVP search header. */
function PhotoCarousel({ images }: { images: CarouselImage[] }) {
  // Duplicate the set so the -50% marquee loop is seamless.
  const strip = [...images, ...images];
  return (
    <div className="relative left-1/2 -mt-12 w-screen -translate-x-1/2 overflow-hidden bg-pine/5">
      <div className="marquee-track flex h-56 w-max animate-[marquee_60s_linear_infinite] sm:h-72">
        {strip.map((img, i) => (
          <img
            key={i}
            src={img.src}
            // Each frame is height-constrained, so its displayed width follows
            // from the aspect ratio — landscape frames need a wider variant
            // than portrait ones at the same strip height.
            srcSet={srcSetFor(img.src, WIDE_WIDTHS, img.w)}
            sizes={sizesForHeights(img.w / img.h, [
              { minWidth: 640, height: 288 },
              { height: 224 },
            ])}
            // Real pixel size → the browser reserves the frame's width from the
            // aspect ratio before the image loads, so the w-max track has its
            // full width at first paint and the marquee never starts stuck.
            width={img.w}
            height={img.h}
            alt=""
            aria-hidden="true"
            // No loading="lazy": the marquee translates the whole strip, so
            // off-screen frames never intersect to load on Safari — their width
            // stays 0, the w-max track collapses, and the animation looks stuck.
            decoding="async"
            // shrink-0 is essential: without it iOS Safari shrinks these flex
            // frames to fit the viewport, collapsing the w-max track so the
            // -50% translate barely moves.
            className="h-full w-auto shrink-0 border-r border-white object-cover"
          />
        ))}
      </div>
    </div>
  );
}

function MemberCard({
  member,
  response,
  onDay,
  onDietary,
}: {
  member: Member;
  response: MemberResponse;
  onDay: (id: string, dayKey: string, value: boolean) => void;
  onDietary: (id: string, dietary: string) => void;
}) {
  const attending = DAYS.some((d) => response.attendance[d.key] === true);
  return (
    // More air between people on narrow screens than on wide ones: the day rows
    // wrap there, so each person's block is much taller and needs a bigger gap
    // to still read as a new person rather than another question.
    <div className="py-8 sm:py-5">
      <h3 className="font-serif text-2xl font-semibold text-pine sm:text-3xl">
        {member.name}
      </h3>

      <div className="mt-5 space-y-5">
        {DAYS.map((day) => (
          <div
            key={day.key}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <p className="text-xl text-pine">
                {day.label}, {day.date}
              </p>
              <p className="text-lg text-pine/70">{day.sub}</p>
            </div>
            <YesNo
              value={response.attendance[day.key] ?? null}
              onYes={() => onDay(member.id, day.key, true)}
              onNo={() => onDay(member.id, day.key, false)}
            />
          </div>
        ))}
      </div>

      {attending && (
        <label className="mt-6 block">
          <span className="text-base uppercase tracking-[0.2em] text-midnight-teal">
            Dietary restrictions
          </span>
          <input
            value={response.dietary}
            onChange={(e) => onDietary(member.id, e.target.value)}
            placeholder="Allergies, preferences, none…"
            className="mt-2 w-full border-b border-pine/25 bg-transparent py-2 text-lg text-pine placeholder:text-pine/45 focus:border-midnight-teal focus:outline-none"
          />
        </label>
      )}
    </div>
  );
}

/** Tap to answer: the chosen word takes on color — teal for Yes, rose for No —
 *  and once one is picked the other fades back to a faint grey. */
function YesNo({
  value,
  onYes,
  onNo,
}: {
  value: Attendance;
  onYes: () => void;
  onNo: () => void;
}) {
  const answered = value !== null;
  return (
    <div className="flex shrink-0 items-center gap-4 font-serif text-xl tracking-[0.1em] [font-variant-caps:all-small-caps] sm:gap-7 sm:text-2xl">
      <Choice
        active={value === true}
        dimmed={answered && value !== true}
        activeClass="text-midnight-teal"
        onClick={onYes}
      >
        Yes
      </Choice>
      <Choice
        active={value === false}
        dimmed={answered && value !== false}
        activeClass="text-dusty-rose"
        onClick={onNo}
      >
        No
      </Choice>
    </div>
  );
}

function Choice({
  active,
  dimmed,
  activeClass,
  onClick,
  children,
}: {
  active: boolean;
  dimmed: boolean;
  activeClass: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const state = active
    ? `${activeClass} font-bold`
    : dimmed
      ? "text-pine/25"
      : "text-pine/40 hover:text-pine/70";
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`px-1.5 py-0.5 transition-colors duration-200 ${state}`}
    >
      {children}
    </button>
  );
}
